import { useState } from "react";
import { Mail, Phone, User, Send, CheckCircle2, Handshake } from "lucide-react";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { Reveal, SectionHeader } from "@/components/shared/Reveal";
import { toast } from "sonner";

export const ContactSection = () => {
  const { data } = useData();
  const contacts = data.contacts || [];
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.createMessage(form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Ditt meddelande har skickats!");
    } catch {
      toast.error("Kunde inte skicka. Försök igen.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full rounded-lg bg-[#14161A] border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-amber-500 focus:outline-none transition-colors";

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeader kicker="Kontakt" title="Kontakta oss" subtitle="Frågor om tävlingen, media eller partnerskap? Hör av dig." />
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            <Reveal>
              <div data-testid="media-contact" className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-amber-500">Samarbeten & Media</div>
                <h3 className="mt-1 font-display text-xl font-bold uppercase text-white flex items-center gap-2">
                  <Handshake className="w-4 h-4 text-amber-500" /> Partnerskap & pressmaterial
                </h3>
                <p className="mt-1 text-sm text-white/50">För samarbeten, sponsring och media – skicka loggor, information och material hit.</p>
                <div className="mt-3">
                  <a href="mailto:novemberkasanmedia@gmail.com" className="inline-flex items-center gap-2 text-white/80 hover:text-amber-500"><Mail className="w-4 h-4" />novemberkasanmedia@gmail.com</a>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="media-accreditation" className="rounded-xl border border-amber-500/30 bg-[#14161A] p-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-amber-500">Media / Press</div>
                <h3 className="mt-1 font-display text-xl font-bold uppercase text-white">Medieackreditering för Novemberkåsan 7 november 2026</h3>
                <div className="mt-3 space-y-3 text-sm text-white/70 leading-relaxed">
                  <p>För att få tillgång till pressrum i anslutning till tävlingsledningen behöver du som arbetar med medierapportering och fotografering ackreditera dig.</p>
                  <p>Enklast gör du det genom att senast 5 november skicka e-post till presschef Sture Jonsson <a href="mailto:sture.jonsson2@gmail.com" data-testid="media-accreditation-email" className="text-amber-500 hover:underline">sture.jonsson2@gmail.com</a> med följande uppgifter:</p>
                  <p>Namn, medieföretag eller organisation, publiceringskanal (t.ex. tidning, radio, tv, blogg), din e-postadress och mobilnummer.</p>
                  <p>Du kommer att få material och ackrediteringsbevis på tävlingsdagen vid ankomsten till tävlingscentrum vid Centrum för Idrott och Kultur i Knivsta.</p>
                </div>
              </div>
            </Reveal>
            {contacts.map((c) => (
              <Reveal key={c.id}>
                <div className="rounded-xl border border-white/8 bg-[#14161A] p-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-amber-500">{c.area || c.role}</div>
                  <h3 className="mt-1 font-display text-xl font-bold uppercase text-white flex items-center gap-2">
                    <User className="w-4 h-4 text-white/40" /> {c.name}
                  </h3>
                  {c.role && <div className="text-sm text-white/50">{c.role}</div>}
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-white/70 hover:text-amber-500"><Mail className="w-4 h-4" />{c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 text-white/70 hover:text-amber-500"><Phone className="w-4 h-4" />{c.phone}</a>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-[#0F1114] p-6 sm:p-8">
              {sent ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle2 className="w-14 h-14 text-amber-500 mx-auto" />
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">Tack!</h3>
                  <p className="mt-2 text-white/60">Vi återkommer så snart vi kan.</p>
                  <button onClick={() => setSent(false)} className="mt-6 text-sm text-amber-500 hover:underline">Skicka ett till</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required placeholder="Namn" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
                    <input required type="email" placeholder="E-post" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
                  </div>
                  <input placeholder="Ämne" data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} />
                  <textarea required placeholder="Meddelande" rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} />
                  <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-black hover:bg-amber-400 transition-colors disabled:opacity-50">
                    <Send className="w-4 h-4" /> {sending ? "Skickar..." : "Skicka meddelande"}
                  </button>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
